import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { FeeValue } from '../../modules/default-fees/types';
import { GetProductDetailsResult } from '../../modules/rate-quote';
import { AllQuotesOffer, Offer, ProductSet } from '../../modules/rate-quote/types';
import {
  createOfferError,
  createOfferRequest,
  createOfferSuccess,
  updateOffer,
  updateOfferError,
  updateOfferSuccess
} from '../rate-quote/slice';
import { defaultOfferDetailsState, EditableOffer, Nullable } from './state';

const priceCalculation = (
  value: FeeValue | undefined,
  id: 'lenderCredit' | 'points',
  loanSize: number
) => {
  if (typeof value !== 'number') {
    return 100;
  }
  return id === 'lenderCredit' ? (value / loanSize) * 100 + 100 : (-value / loanSize) * 100 + 100;
};

const lcPointsCalculation = (price: number, loanSize: number) =>
  Math.ceil(((price - 100) / 100) * loanSize);

export const offerDetailsSlice = createSlice({
  name: 'offerDetails',
  initialState: defaultOfferDetailsState,
  reducers: {
    seedOffer: (
      state,
      {
        payload: { offer, allQuotes }
      }: PayloadAction<{ offer: Offer | AllQuotesOffer; allQuotes?: boolean }>
    ) => ({
      ...state,
      modalOffer: {
        ...(offer as any),
        attributes: {
          ...offer.attributes,
          loanSize: offer.attributes.loanSize - offer.attributes.amtToFinance
        }
      },
      ...(allQuotes ? { isOpenAllQuotes: true } : { isOpen: true })
    }),
    updateOfferDetailsFee: (
      { modalOffer, ...rest },
      { payload: { id, value } }: PayloadAction<{ id: string; value: number | null | undefined }>
    ) => {
      let price = modalOffer!.attributes.price;
      if (id === 'lenderCredit' || id === 'points') {
        const defaultValue = modalOffer!.fees.find(({ id: feeId }) => feeId === id)?.computedValue;
        price =
          value == null
            ? priceCalculation(defaultValue, id, modalOffer!.attributes.loanSize)
            : priceCalculation(value, id, modalOffer!.attributes.loanSize);
      }
      return {
        ...rest,
        modalOffer: {
          ...modalOffer!,
          attributes: {
            ...modalOffer!.attributes,
            price
          },
          fees: modalOffer!.fees.map((fee) =>
            fee.id === id ? { ...fee, manualValue: value } : fee
          )
        },
        hasChanged: true
      };
    },
    seedInitialOffer: (
      state,
      {
        payload: {
          lenderId,
          pmi,
          productSet: {
            loanType,
            helocLine,
            closingCostOption,
            closingToCover,
            interestOptions,
            escrowOptions,
            product: { dti, ...restProduct },
            ltv,
            conforming,
            lenders: {
              [lenderId]: {
                fees,
                id,
                name,
                url,
                nmls,
                rateLockDays,
                armCaps,
                indexRate,
                mi,
                amtToFinance,
                upFrontInsuranceFee
              }
            },
            ...rest
          },
          sellerConcessions
        }
      }: PayloadAction<{
        lenderId: string;
        productSet: ProductSet;
        pmi: number | undefined;
        sellerConcessions?: number | null;
      }>
    ) => ({
      ...state,
      modalOffer: {
        starred: true,
        offerId: uuid(),
        type: 'downPayment' in rest ? 'purchase' : 'refinance',
        attributes: {
          lender: lenderId,
          fullLender: {
            id,
            name,
            url,
            nmls
          },
          product: restProduct,
          loanType,
          helocLine,
          closingCostOption,
          closingToCover,
          interestOptions,
          escrowOptions,
          amtToFinance,
          ltv,
          price: 100,
          conforming,
          ...('downPayment' in rest && { downPayment: rest.downPayment }),
          ...('cashOut' in rest && { cashOut: rest.cashOut }),
          ...('existingLoans' in rest && { existingLoans: rest.existingLoans }),
          ...('totalCashOut' in rest && { totalCashOut: rest.totalCashOut }),
          ...('loanAmount' in rest && { loanSize: rest.loanAmount }),
          ...('irrrlEligible' in rest && { irrrlEligible: rest.irrrlEligible }),
          ...(upFrontInsuranceFee !== undefined && { upFrontInsuranceFee })
        } as EditableOffer['attributes'],
        monthlyPayment: {
          mi,
          ...(pmi ? { pmi } : {})
        },
        productDetails: {
          label: '',
          rate: null,
          manualRateLockDays: null,
          ...('adjustmentPeriod' in restProduct && {
            armCaps: null,
            margin: null,
            indexType: null
          })
        },
        lender: {
          id,
          name,
          url,
          nmls
        },
        fees: fees
          .filter(({ value }) => value !== 'NotIncluded')
          .map(({ value, ...r }) => ({
            ...r,
            computedValue: value,
            manualValue: null
          })),
        computedProductDetails: {
          rateLockDays,
          armCaps,
          indexRate,
          prepaidInterest: 0,
          apr: 0,
          dti: 0
        },
        manualFees: [],
        lastModifiedTime: Date.now(),
        method: state.modalOffer?.method ? state.modalOffer?.method : 'Manual',
        expired: false,
        locked: false,
        sellerConcessions,
        adjustments: [],
        notesAndAdvisories: []
      },
      hasChanged: false,
      loading: false,
      isOpen: true
    }),
    updatePrice: (
      { modalOffer, ...rest },
      { payload }: PayloadAction<{ price: number | undefined }>
    ) => {
      let fees = modalOffer!.fees;
      if (!!payload.price) {
        const lcPoints = lcPointsCalculation(payload.price, modalOffer!.attributes.loanSize);
        fees = fees.map((fee) => {
          switch (fee.id) {
            case 'lenderCredit':
              return { ...fee, manualValue: lcPoints > 0 ? lcPoints : 0 };
            case 'points':
              return { ...fee, manualValue: lcPoints > 0 ? 0 : Math.abs(lcPoints) };
            default:
              return fee;
          }
        });
      }
      return {
        ...rest,
        modalOffer: {
          ...modalOffer!,
          attributes: {
            ...modalOffer!.attributes,
            price: payload.price
          },
          fees
        },
        hasChanged: true
      };
    },
    updateOfferDetailsRateInfo: (
      { modalOffer, ...rest },
      { payload }: PayloadAction<Partial<Nullable<Offer['productDetails']>>>
    ) => ({
      ...rest,
      modalOffer: {
        ...modalOffer!,
        productDetails: { ...modalOffer!.productDetails, ...payload } as Offer['productDetails']
      },
      hasChanged: true
    }),
    updateOfferDetailsPMI: (
      { modalOffer, ...rest },
      { payload }: PayloadAction<number | undefined>
    ) => ({
      ...rest,
      modalOffer: {
        ...modalOffer!,
        monthlyPayment: {
          ...modalOffer!.monthlyPayment,
          mi: {
            ...modalOffer!.monthlyPayment.mi!,
            value: undefined
          },
          pmi: payload
        }
      },
      hasChanged: true
    }),
    validateOfferDetailsForm: (state) => ({
      ...state,
      validateForm: true
    }),
    closeOfferDetails: (state) => ({
      ...state,
      modalOffer: null,
      isOpen: false,
      isOpenAllQuotes: false,
      networkError: null,
      validateForm: false
    }),
    restoreModalStateCache: (
      state,
      { payload: { offer, allQuotes } }: PayloadAction<{ offer: EditableOffer; allQuotes: boolean }>
    ) => ({
      ...state,
      ...(allQuotes ? { isOpenAllQuotes: true } : { isOpen: true }),
      modalOffer: offer
    }),
    getProductDetails: (state, _: PayloadAction<{ productId: number; searchId: string }>) => ({
      ...state,
      productDetailsStatus: 'loading' as const
    }),
    getProductDetailsSuccess: (state, { payload }: PayloadAction<GetProductDetailsResult>) => {
      if (state.modalOffer) {
        return {
          ...state,
          modalOffer: {
            ...state.modalOffer,
            ...payload
          },
          productDetailsStatus: 'loaded' as const
        };
      }
      return { ...state, isOpenAllQuotes: true };
    },
    getProductDetailsFailure: (state) => {
      if (state.modalOffer) {
        return {
          ...state,
          modalOffer: {
            ...state.modalOffer,
            notesAndAdvisories: [],
            adjustments: []
          },
          productDetailsStatus: 'error' as const
        };
      }
      return state;
    },
    cacheModalState: (state, _: PayloadAction<{ offer: EditableOffer; allQuotes: boolean }>) =>
      state,
    clearModalStateCache: (state) => state
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOfferRequest, (state) => ({
        ...state,
        validateForm: false,
        networkError: null,
        loading: true
      }))
      .addCase(createOfferSuccess, (state) => ({
        ...state,
        modalOffer: null,
        loading: false,
        isOpen: false
      }))
      .addCase(createOfferError, (state, { payload }) => ({
        ...state,
        loading: false,
        networkError: payload,
        validateForm: true
      }))
      .addCase(updateOffer, (state) => ({
        ...state,
        validateForm: false,
        networkError: null,
        loading: true
      }))
      .addCase(updateOfferSuccess, (state) => ({
        ...state,
        modalOffer: null,
        loading: false,
        isOpen: false
      }))
      .addCase(updateOfferError, (state, { payload }) => ({
        ...state,
        loading: false,
        networkError: payload,
        validateForm: true
      }));
  }
});

export const {
  seedOffer,
  updateOfferDetailsFee,
  seedInitialOffer,
  updatePrice,
  updateOfferDetailsRateInfo,
  updateOfferDetailsPMI,
  validateOfferDetailsForm,
  closeOfferDetails,
  restoreModalStateCache,
  getProductDetails,
  getProductDetailsSuccess,
  getProductDetailsFailure,
  cacheModalState,
  clearModalStateCache
} = offerDetailsSlice.actions;

export const reducer = offerDetailsSlice.reducer;
